/**
 * Date/time formatting utilities — all output in Pacific Time (America/Los_Angeles).
 * Automatically handles PST (UTC-8, Nov–Mar) and PDT (UTC-7, Mar–Nov).
 *
 * Input contract: ALL timestamps coming from the API are UTC.
 * If a string has no timezone indicator (no Z, no +00:00, no offset),
 * we append 'Z' so JavaScript parses it as UTC rather than local time.
 */

const TZ = 'America/Los_Angeles';

/**
 * Normalise any UTC timestamp to a Date object.
 * Handles strings with/without Z suffix, space-separated, epoch ms, Date instances.
 */
function toDate(value) {
  if (!value) return null;
  if (value instanceof Date) return isNaN(value.getTime()) ? null : value;
  if (typeof value === 'number') {
    const d = new Date(value);
    return isNaN(d.getTime()) ? null : d;
  }
  if (typeof value === 'string') {
    // If the string looks like an ISO datetime with no timezone suffix, treat as UTC
    const hasOffset = /Z$|[+-]\d{2}:\d{2}$|[+-]\d{4}$/.test(value);
    const normalised = hasOffset ? value : value.replace(' ', 'T') + 'Z';
    const d = new Date(normalised);
    return isNaN(d.getTime()) ? null : d;
  }
  return null;
}

/**
 * Time only — e.g. "10:23:45 AM PDT"
 */
export function formatTimePT(value, opts = {}) {
  const d = toDate(value);
  if (!d) return '—';
  return d.toLocaleTimeString('en-US', {
    timeZone: TZ,
    hour: '2-digit',
    minute: '2-digit',
    second: opts.noSeconds ? undefined : '2-digit',
    hour12: true,
    timeZoneName: 'short',
    ...opts,
  });
}

/**
 * Short time only (no seconds, no timezone label) — e.g. "10:23 AM"
 */
export function formatShortTimePT(value) {
  const d = toDate(value);
  if (!d) return '—';
  return d.toLocaleTimeString('en-US', {
    timeZone: TZ,
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
}

/**
 * Date only — e.g. "May 27, 2026"
 */
export function formatDatePT(value) {
  const d = toDate(value);
  if (!d) return '—';
  return d.toLocaleDateString('en-US', {
    timeZone: TZ,
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

/**
 * Date + time — e.g. "May 27, 2026, 10:23 AM PDT"
 */
export function formatDateTimePT(value) {
  const d = toDate(value);
  if (!d) return '—';
  return d.toLocaleString('en-US', {
    timeZone: TZ,
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
    timeZoneName: 'short',
  });
}

/**
 * Date + time with seconds — e.g. "May 27, 2026, 10:23:45 AM PDT"
 */
export function formatDateTimeSecsPT(value) {
  const d = toDate(value);
  if (!d) return '—';
  return d.toLocaleString('en-US', {
    timeZone: TZ,
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
    timeZoneName: 'short',
  });
}

/**
 * Short date + time (no year) — e.g. "May 27, 10:23 AM PDT"
 */
export function formatShortDateTimePT(value) {
  const d = toDate(value);
  if (!d) return '—';
  return d.toLocaleString('en-US', {
    timeZone: TZ,
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
    timeZoneName: 'short',
  });
}
